import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../../common/Modal'
import { nowDate } from '../../../../utils/date'
import { ValueOf } from '../../../../utils/type'
import iconv = require('iconv-lite')
import Encoding from 'encoding-japanese'
import { useAlert } from 'react-alert'

// eslint-disable-next-line @typescript-eslint/no-var-requires
const FileDownload = require('js-file-download')

interface MemberCost {
  period_start: string | null
  period_end: string | null
  hourly_pay: number
}

interface Member {
  id: number
  name: string
  member_costs: MemberCost[]
}

interface UpdateMemberCosts {
  member_id: number
  member_costs: {
    hourly_pay: number
    period_start: string | null
    period_end: string | null
  }[]
}

interface Props {
  isDisplay: boolean
  memberList: Member[]
  closeCallback: VoidFunction
  changeCallback: (data: UpdateMemberCosts[]) => void
}

interface ParseRow {
  member_id: number
  name: string
  before_member_costs: MemberCost[]
  after_member_costs: MemberCost[]
}

const CSV_HEADER = "ユーザーID(変更禁止),名前(変更禁止),ユーザーコスト"

const toIsoDate = (value: string): string | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (!trimmed) return null
  if (!/^\d{4}\/\d{1,2}\/\d{1,2}$/.test(trimmed)) return ""
  const [y, m, d] = trimmed.split('/')
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}

const toCsvDate = (value: string | null): string => {
  if (!value) return ""
  return value.replace(/-/g, '/')
}

const stringifyCosts = (costs: MemberCost[]): string => (
  costs.map((c) => (
    `${toCsvDate(c.period_start)}$${toCsvDate(c.period_end)}$${c.hourly_pay}`
  )).join(':')
)

const STEP = {
  DOWNLOAD: 1,
  UPLOAD: 2,
  CONFIRM: 3,
} as const
type Step = ValueOf<typeof STEP>

const isChanged = (before: MemberCost[], after: MemberCost[]): boolean => {
  if (before.length !== after.length) return true
  return before.some((b, i) => (
    b.period_start !== after[i].period_start
    || b.period_end !== after[i].period_end
    || b.hourly_pay !== after[i].hourly_pay
  ))
}

const formatPeriod = (start: MemberCost['period_start'], end: MemberCost['period_end']): string => {
  const s = start ? toCsvDate(start) : "指定なし"
  const e = end ? toCsvDate(end) : "指定なし"
  return `${s} 〜 ${e}`
}

// NOTE: 1行ごとにパースし、エラーは即時 return で打ち切る。row が null の行は変更対象外
type ParseLineResult = { error: string } | { row: ParseRow | null }

const parseLine = (line: string, rowNumber: number, memberList: Member[]): ParseLineResult => {
  const cols = line.split('","').map((v) => v.replace(/"/g, ''))
  if (cols.length < 2) return { row: null }
  const memberId = Number(cols[0])
  if (!memberId) return { error: `${rowNumber}行目: ユーザーIDが不正です` }
  const member = memberList.find((m) => m.id === memberId)
  if (!member) return { error: `${rowNumber}行目: ユーザーID ${cols[0]} が見つかりません` }

  const costsField = cols[2] ?? ""
  const afterCosts: MemberCost[] = []
  if (costsField.trim()) {
    const segments = costsField.split(':')
    for (let s = 0;s < segments.length;s += 1) {
      const parts = segments[s].split('$')
      if (parts.length !== 3) return { error: `${rowNumber}行目: ユーザーコストの形式が不正です` }
      const periodStart = toIsoDate(parts[0])
      const periodEnd = toIsoDate(parts[1])
      if (periodStart === "" || periodEnd === "") return { error: `${rowNumber}行目: 日付の形式が不正です (YYYY/MM/DD)` }
      const payRaw = parts[2].trim()
      if (!/^\d+$/.test(payRaw)) return { error: `${rowNumber}行目: 1時間あたりのコストが不正です` }
      afterCosts.push({ period_start: periodStart, period_end: periodEnd, hourly_pay: Number(payRaw) })
    }
  }

  if (!isChanged(member.member_costs, afterCosts)) return { row: null }
  return {
    row: {
      member_id: memberId,
      name: member.name,
      before_member_costs: member.member_costs,
      after_member_costs: afterCosts,
    },
  }
}

interface CostBlockProps {
  title: string
  costs: MemberCost[]
  emptyLabel: string
}

const CostBlock: FC<CostBlockProps> = ({ title, costs, emptyLabel }) => (
  <div className="block_area">
    <div className="title_area">{title}</div>
    <div className="cost_block_list">
      {costs.length ? (
        costs.map((c) => (
          <div key={`${c.period_start}_${c.period_end}_${c.hourly_pay}`} className="cost_block_item">
            <div>{formatPeriod(c.period_start, c.period_end)}</div>
            <div>{`¥${c.hourly_pay.toLocaleString()}`}</div>
          </div>
        ))
      ) : (
        <div className="cost_block_empty">{emptyLabel}</div>
      )}
    </div>
  </div>
)

const ImportMemberCostModal: FC<Props> = (props) => {
  const {
    isDisplay,
    memberList,
    closeCallback,
    changeCallback,
  } = props

  const alert = useAlert()

  const [fileData, setFileData] = useState("")
  const [step, setStep] = useState<Step>(STEP.DOWNLOAD)
  const [errorMessage, setErrorMessage] = useState<string>("")

  const parseResult = useMemo<{ rows: ParseRow[]; error: string }>(() => {
    if (!fileData) return { rows: [], error: "" }
    const lines = fileData.split(/\r?\n/).filter((line) => line.length > 0)
    if (!lines.length) return { rows: [], error: "CSVが空です" }
    const header = lines[0].replace(/"/g, '')
    if (header !== CSV_HEADER) {
      return { rows: [], error: "指定外のCSVファイルを検知しました" }
    }
    const dataLines = lines.slice(1)
    const rows: ParseRow[] = []
    for (let i = 0;i < dataLines.length;i += 1) {
      const result = parseLine(dataLines[i], i + 2, memberList)
      if ('error' in result) return { rows: [], error: result.error }
      if (result.row) rows.push(result.row)
    }
    if (!rows.length) {
      return { rows: [], error: "変更対象のデータがありませんでした" }
    }
    return { rows, error: "" }
  }, [fileData, memberList])

  useEffect(() => {
    setErrorMessage(parseResult.error)
    if (!parseResult.error && parseResult.rows.length && step === STEP.UPLOAD) {
      setStep(STEP.CONFIRM)
    }
  }, [parseResult])

  const buildData = useMemo<UpdateMemberCosts[]>(() => (
    parseResult.rows.map((parsed) => ({
      member_id: parsed.member_id,
      member_costs: parsed.after_member_costs.map((parsed_member_cost) => ({
        hourly_pay: parsed_member_cost.hourly_pay,
        period_start: parsed_member_cost.period_start,
        period_end: parsed_member_cost.period_end,
      })),
    }))
  ), [parseResult.rows])

  const downloadCsv = (code: string) => {
    const rows = memberList.map((member) => (
      `"${member.id}","${member.name}","${stringifyCosts(member.member_costs)}"`
    ))
    rows.unshift(CSV_HEADER)
    const filename = `ユーザーコスト一括登録_${nowDate("YYYYMMDD")}`.slice(0, 150)
    if (code === 'Shift-JIS') {
      const encodeData = iconv.encode(rows.join('\n'), "windows-31j")
      FileDownload(new Blob([encodeData]), `${filename}.csv`)
    } else {
      FileDownload(rows.join('\n'), `${filename}.csv`)
    }
  }

  const uploadCsv = (uploadFile: FileList | null) => {
    setErrorMessage("")
    setFileData("")
    if (!uploadFile || uploadFile[0].type !== "text/csv") {
      setErrorMessage("不正なファイルを検知しました")
      return
    }
    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target === null || e.target.result === null) return
      const buf = new Uint8Array(e.target.result as ArrayBuffer)
      const codes = Encoding.detect(new Uint8Array(buf))
      if (codes === 'UNICODE' || codes === 'SJIS') {
        setFileData(iconv.decode(Buffer.from(buf), "windows-31j"))
        return
      }

      setFileData(new TextDecoder("utf-8").decode(buf))
    }
    reader.onerror = () => alert.error("不正なファイルが読み込まれました")
    reader.readAsArrayBuffer(uploadFile[0])
  }

  const prev = () => {
    setStep((s) => (s - 1) as Step)
    setFileData("")
    setErrorMessage("")
  }

  const submitCost = () => {
    if (!buildData.length) return
    changeCallback(buildData)
    closeCallback()
  }

  const buildButtonArea = () => {
    let prevButton = (
      <button
        type="button"
        className="button cancel"
        onClick={prev}
      >
        戻る
      </button>
    )
    let nextButton = (
      <button
        type="button"
        className="button"
        onClick={() => setStep((s) => (s + 1) as Step)}
        disabled={step === STEP.UPLOAD && !buildData.length}
      >
        次へ
      </button>
    )
    if (step === STEP.DOWNLOAD) {
      prevButton = <></>
    }
    if (step === STEP.UPLOAD) {
      nextButton = <></>
    }
    if (step === STEP.CONFIRM) {
      nextButton = (
        <button
          type="button"
          className="button"
          onClick={submitCost}
        >
          変更する
        </button>
      )
    }
    return (
      <div className="button_area">
        {prevButton}
        {nextButton}
      </div>
    )
  }

  useEffect(() => {
    setStep(STEP.DOWNLOAD)
    setFileData("")
    setErrorMessage("")
  }, [isDisplay])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="import_member_cost"
      closeCallback={closeCallback}
      closeable
    >
      <>
        <div className="modal_title">
          ユーザーコストを一括登録する
        </div>
        {
          step === STEP.DOWNLOAD && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.1 ユーザーコスト一括登録用CSVファイルのダウンロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      ユーザーコスト一括登録用CSVファイルのダウンロードをおこない、編集してください。
                    </div>
                    <div className="red">
                      ※ExcelでCSVを開く方はShift-JISを選択して下さい。
                    </div>
                  </div>
                  <div className="detail_button_area">
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('Shift-JIS')}
                    >
                      ユーザーコスト一括登録用(Shift-JIS)
                    </button>
                    <button
                      type="button"
                      className="download"
                      onClick={() => downloadCsv('UTF-8')}
                    >
                      ユーザーコスト一括登録用(UTF-8)
                    </button>
                  </div>
                </div>
                <div className="step_title">
                  STEP.2 ユーザーコスト一括登録用CSVファイルの編集
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      <p>
                        ダウンロードしたユーザーコスト一括登録用CSVファイルを編集してください。
                      </p>
                    </div>
                    <div
                      style={{
                        maxHeight: "calc(83vh - 370px)",
                        overflowY: "scroll",
                      }}
                    >
                      <table>
                        <tr>
                          <th>
                            項目名
                          </th>
                          <th>
                            必須 / 任意
                          </th>
                          <th>
                            概要
                          </th>
                        </tr>
                        <tr>
                          <td>
                            ユーザーID(変更禁止)
                          </td>
                          <td>
                            -
                          </td>
                          <td>
                            <p className="required">
                              この項目に記入してあるユーザーIDで一致判定を行い、記入したユーザーコストの設定値で置き換えます。
                            </p>
                            <p>
                              対応するユーザーの行を削除すると、そのユーザーのユーザーコストは変更されません。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            名前(変更禁止)
                          </td>
                          <td>
                            -
                          </td>
                          <td>
                            <p>
                              ユーザーIDに対応する名前が記載されています。
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            ユーザーコスト
                          </td>
                          <td>
                            任意
                          </td>
                          <td>
                            <p>
                              ユーザーに設定したいユーザーコストの適用開始日・適用終了日・1時間あたりのコスト(円)を入力してください。
                            </p>
                            <p>
                              設定したいときは適用開始日・適用終了日・1時間あたりのコスト(円)の間に「$」を入れてください。
                            </p>
                            <p>
                              また、複数設定したいときは間に「:」を入れてください。
                            </p>
                            <p className="example">
                              例) 2025/12/01$2026/05/31$1000:2026/06/01$$2000
                            </p>
                            <p className="required">
                              ※既存のユーザーコストの設定値は削除されます。
                            </p>
                          </td>
                        </tr>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )
        }
        {
          step === STEP.UPLOAD && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.3 ユーザーコスト一括登録用CSVファイルのアップロード
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      編集したユーザーコスト一括登録用CSVファイルをアップロードしてください。
                    </div>
                  </div>
                  <div className="upload_area">
                    <label htmlFor="import_csv">
                      ユーザーコスト一括登録用CSVファイルをアップロードしてください。
                      <input
                        type="file"
                        id="import_csv"
                        name="import_csv"
                        accept="text/csv"
                        onChange={(e) => uploadCsv(e.currentTarget.files)}
                        onClick={(e) => { e.currentTarget.value = "" }}
                      />
                    </label>
                  </div>
                  {
                    errorMessage ? (
                      <div className="error_message">
                        {errorMessage}
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
              </div>
            </>
          )
        }
        {
          step === STEP.CONFIRM && (
            <>
              <div className="modal_detail">
                <div className="step_title">
                  STEP.4 変更する情報の確認
                </div>
                <div className="step_detail">
                  <div className="detail_text_area">
                    <div>
                      以下の内容で変更をおこないます。
                    </div>
                  </div>
                  <div className="detail_result_area">
                    {
                      parseResult.rows.map((data) => (
                        <div key={`data_list_${data.member_id}`} className="result_row">
                          <div className="name_area">
                            {data.name}
                          </div>
                          <div className="detail_area">
                            <CostBlock title="変更前" costs={data.before_member_costs} emptyLabel="設定なし" />
                            <CostBlock title="変更後" costs={data.after_member_costs} emptyLabel="未設定" />
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </>
          )
        }
        {buildButtonArea()}
      </>
    </Modal>
  )
}

export default ImportMemberCostModal
